import React, { useState, useEffect, useCallback, useRef } from "react";
import PromptInput from "./PromptInput";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Key, BookOpen, BarChart3, Code2, Globe, Shield,
  Copy, Plus, Trash2, RefreshCw, Eye, EyeOff,
  CheckCircle, AlertTriangle, Loader2, Play, ChevronRight,
  Activity, Clock, Zap, ExternalLink, Terminal,
  Webhook, Rocket, StopCircle, Users, Hash, Wheat,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied!")).catch(() => toast.error("Copy failed"));
}

// ==================== API Keys Section (shared with HAVEN) ====================
function APIKeysSection() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/keys`);
      setKeys(res.data.keys || []);
    } catch { /* non-blocking */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    if (!newKeyName.trim()) { toast.error("Enter a key name"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/keys/create`, { name: newKeyName.trim(), permissions: "full" });
      setRevealedKey(res.data.key);
      setNewKeyName("");
      toast.success("API key created! Copy it now — it won't be shown again.");
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    }
    setCreating(false);
  };

  const revokeKey = async (preview) => {
    try {
      await axios.post(`${API}/keys/revoke`, { key_preview: preview });
      toast.success("Key revoked");
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="harvest-api-keys-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4 text-amber-400" /> Create API Key
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input placeholder="Key name (e.g., harvest-prod-server)" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)] flex-1" data-testid="harvest-key-name-input"
              onKeyDown={(e) => e.key === "Enter" && createKey()} />
            <Button onClick={createKey} disabled={creating} className="bg-amber-600 hover:bg-amber-700" data-testid="harvest-create-key-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-4 h-4 mr-2" />Create</>}
            </Button>
          </div>
          <AnimatePresence>
            {revealedKey && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="harvest-revealed-key">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-400 font-medium mb-1">New API Key (copy now!)</p>
                    <code className="text-sm font-mono text-[var(--qn-foreground)] break-all">{revealedKey}</code>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(revealedKey)} className="shrink-0 ml-3"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Your API Keys</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchKeys} className="h-7 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[var(--qn-foreground-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading keys...</div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-[var(--qn-foreground-muted)] py-4 text-center">No API keys yet. Create one above to get started.</p>
          ) : (
            <div className="space-y-2">
              {keys.map((k, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]" data-testid={`harvest-key-${k.key_preview}`}>
                  <div className="flex items-center gap-3">
                    <Key className="w-4 h-4 text-amber-400" />
                    <div>
                      <span className="text-sm font-medium text-[var(--qn-foreground)]">{k.name}</span>
                      <div className="text-xs font-mono text-[var(--qn-foreground-muted)]">{k.key_preview}...****</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{k.permissions || "full"}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => revokeKey(k.key_preview)} className="h-7 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Interactive Docs Section ====================
function InteractiveDocsSection() {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/harvest/docs`).then(r => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const testEndpoint = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.get(`${API}/harvest/divisions`);
      setTestResult({ status: res.status, data: JSON.stringify(res.data, null, 2).slice(0, 500) + "..." });
    } catch (e) {
      setTestResult({ status: e.response?.status || 500, data: JSON.stringify(e.response?.data || { error: e.message }, null, 2) });
    }
    setTesting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin mr-2 text-[var(--qn-foreground-muted)]" />Loading docs...</div>;

  const endpoints = docs?.endpoints || [];

  return (
    <div className="space-y-4" data-testid="harvest-interactive-docs">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-1">Base URL</p>
              <code className="text-sm font-mono text-amber-400">{process.env.REACT_APP_BACKEND_URL}/api/harvest</code>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/harvest`)}><Copy className="w-3.5 h-3.5" /></Button>
          </div>
          {docs?.authentication && (
            <div className="mt-3 p-2 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]">
              <p className="text-xs text-[var(--qn-foreground-muted)]">Authentication: <code className="text-amber-400">{docs.authentication.header}</code></p>
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Terminal className="w-4 h-4 text-amber-400" /> Endpoints ({endpoints.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {endpoints.map((ep, i) => (
            <div key={i} className="p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]" data-testid={`harvest-endpoint-${i}`}>
              <div className="flex items-center gap-3">
                <Badge className={`text-[10px] font-mono ${ep.method === "POST" ? "bg-amber-600" : "bg-blue-600"}`}>{ep.method}</Badge>
                <code className="text-xs font-mono text-[var(--qn-foreground)]">{ep.path}</code>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mt-1.5">{ep.description}</p>
              {ep.body && (
                <pre className="mt-2 p-2 rounded bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto">{JSON.stringify(ep.body, null, 2)}</pre>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Play className="w-4 h-4 text-amber-400" /> Try It</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={testEndpoint} disabled={testing} className="bg-amber-600 hover:bg-amber-700" data-testid="harvest-test-endpoint-btn">
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}GET /api/harvest/divisions
          </Button>
          {testResult && (
            <div className="mt-3">
              <Badge className={testResult.status < 300 ? "bg-emerald-600" : "bg-red-600"}>HTTP {testResult.status}</Badge>
              <pre className="mt-2 p-3 rounded-lg bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto max-h-[250px]">{testResult.data}</pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Analytics Section ====================
function AnalyticsSection() {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/harvest/executions?limit=10`).then(r => setExecutions(r.data.executions || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="harvest-analytics-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-400" /> HARVEST Execution History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin mr-2 text-[var(--qn-foreground-muted)]" />Loading...</div>
          ) : executions.length === 0 ? (
            <div className="text-center py-8">
              <Wheat className="w-8 h-8 text-amber-400/40 mx-auto mb-3" />
              <p className="text-sm text-[var(--qn-foreground-muted)]">No HARVEST executions yet. Use the Playground to run your first division.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {executions.map((e, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]">
                  <div>
                    <span className="text-xs font-medium text-[var(--qn-foreground)]">{e.swarm_name || "HARVEST Execution"}</span>
                    <div className="text-[10px] text-[var(--qn-foreground-muted)] font-mono mt-0.5">{e.execution_id}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[9px] ${e.status === "completed" ? "bg-amber-600" : e.status === "running" ? "bg-blue-600" : "bg-red-600"}`}>{e.status}</Badge>
                    {e.total_cost !== undefined && <span className="text-[10px] text-[var(--qn-foreground-muted)]">${Number(e.total_cost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Embed Widget Section ====================
function EmbedWidgetSection() {
  const embedCode = `<script src="${process.env.REACT_APP_BACKEND_URL}/api/harvest/widget.js"></script>
<div id="qnneal-harvest-widget"
  data-api-key="YOUR_API_KEY"
  data-division="demand-intelligence"
  data-theme="dark">
</div>`;

  const npmCode = `npm install @qnneal/harvest-sdk

import { HarvestClient } from '@qnneal/harvest-sdk';

const client = new HarvestClient({
  apiKey: 'qng_sk_YOUR_API_KEY',
  baseUrl: '${process.env.REACT_APP_BACKEND_URL}/api/harvest',
});

// Execute a division
const result = await client.execute({
  division: 'demand-intelligence',
  prompt: 'Analyze Q3 2026 fresh produce demand...',
});`;

  return (
    <div className="space-y-4" data-testid="harvest-embed-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Code2 className="w-4 h-4 text-amber-400" /> HTML Widget Embed</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="p-3 rounded-lg bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto">{embedCode}</pre>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => copyToClipboard(embedCode)}><Copy className="w-3.5 h-3.5 mr-1.5" />Copy</Button>
        </CardContent>
      </Card>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Terminal className="w-4 h-4 text-amber-400" /> NPM SDK</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="p-3 rounded-lg bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto">{npmCode}</pre>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => copyToClipboard(npmCode)}><Copy className="w-3.5 h-3.5 mr-1.5" />Copy</Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Webhooks Section ====================
function WebhooksSection() {
  return (
    <div className="space-y-4" data-testid="harvest-webhooks-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Webhook className="w-4 h-4 text-amber-400" /> Webhook Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[var(--qn-foreground-muted)] mb-4">
            Receive real-time notifications when HARVEST executions complete. Configure webhooks to integrate with your supply chain management systems.
          </p>
          <div className="space-y-3">
            {[
              { event: "harvest.execution.completed", desc: "Fired when a division or full swarm execution finishes" },
              { event: "harvest.execution.failed", desc: "Fired when an execution encounters a critical error" },
              { event: "harvest.division.progress", desc: "Fired for each agent completion within a division (batch)" },
              { event: "harvest.alert.spoilage", desc: "Fired when cold chain or shelf-life alerts are triggered" },
            ].map((wh) => (
              <div key={wh.event} className="p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]">
                <code className="text-xs font-mono text-amber-400">{wh.event}</code>
                <p className="text-[10px] text-[var(--qn-foreground-muted)] mt-1">{wh.desc}</p>
              </div>
            ))}
          </div>
          <pre className="mt-4 p-3 rounded-lg bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto">
{`POST https://your-server.com/webhooks/harvest
Authorization: Bearer YOUR_WEBHOOK_SECRET
Content-Type: application/json

{
  "event": "harvest.execution.completed",
  "execution_id": "exec_...",
  "division": "demand-intelligence",
  "agent_count": 100,
  "total_cost": 0.0842,
  "timestamp": "2026-04-09T..."
}`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Execution Playground ====================
const DIVISIONS = [
  { value: "demand-intelligence", label: "Demand Intelligence (100 agents)" },
  { value: "farm-to-warehouse", label: "Farm-to-Warehouse Logistics (100 agents)" },
  { value: "warehouse-inventory", label: "Warehouse & Inventory (100 agents)" },
  { value: "last-mile-distribution", label: "Last-Mile Distribution (100 agents)" },
  { value: "retail-markdown", label: "Retail & Markdown (100 agents)" },
  { value: "food-safety", label: "Food Safety & Traceability (100 agents)" },
  { value: "supplier-network", label: "Supplier Network (100 agents)" },
  { value: "cold-chain", label: "Cold Chain Engineering (100 agents)" },
  { value: "analytics-reporting", label: "Analytics & Reporting (100 agents)" },
  { value: "platform-orchestration", label: "Platform Orchestration (100 agents)" },
];

const SAMPLE_PROMPTS = [
  "Analyze fresh produce demand patterns across North American retail chains for Q3 2026, accounting for summer seasonal spikes and projected heat wave impacts on supply",
  "Optimize cold chain routing for temperature-sensitive dairy products from Wisconsin farms to Southeast distribution centers, minimizing spoilage risk below 2%",
  "Run food safety traceability audit for imported seafood supply chain, checking FDA FSMA 204 compliance across all touchpoints from harvest to retail shelf",
  "Evaluate last-mile delivery efficiency for meal kit subscriptions in urban areas, comparing multi-depot vs single-depot routing with time-window constraints",
];

function ExecutionPlayground() {
  const [division, setDivision] = useState("demand-intelligence");
  const [prompt, setPrompt] = useState("");
  const [executing, setExecuting] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const eventSourceRef = useRef(null);
  const eventsEndRef = useRef(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [events]);

  useEffect(() => {
    return () => { eventSourceRef.current?.close(); };
  }, []);

  const startExecution = async () => {
    setExecuting(true);
    setEvents([]);
    setSummary(null);
    setError(null);

    try {
      const res = await axios.post(`${API}/harvest/execute`, { division, prompt });
      const execId = res.data.execution_id;
      setExecutionId(execId);
      setEvents([{ type: "system", text: `Execution ${execId} started — ${res.data.agent_count} agents`, ts: new Date() }]);

      const es = new EventSource(`${API}/harvest/executions/${execId}/stream`, { withCredentials: true });
      eventSourceRef.current = es;

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "agent_complete" || data.type === "agent_result") {
            setEvents(prev => [...prev, {
              type: "agent", text: data.node_label || data.node_id,
              model: data.model, tokens: data.tokens,
              detail: (data.output || "").slice(0, 120),
              mediaUrl: data.media_url ? `${process.env.REACT_APP_BACKEND_URL}${data.media_url}` : null,
              ts: new Date(),
            }]);
          } else if (data.type === "execution_complete") {
            setEvents(prev => [...prev, { type: "complete", text: `Execution complete: ${data.status}`, ts: new Date() }]);
            setExecuting(false);
            es.close();
            axios.get(`${API}/harvest/executions/${execId}`).then(r => setSummary(r.data)).catch(() => {});
          } else if (data.type === "error") {
            setEvents(prev => [...prev, { type: "error", text: data.message || "Agent error", ts: new Date() }]);
          }
        } catch {}
      };

      es.onerror = () => {
        es.close();
        if (executing) {
          setEvents(prev => [...prev, { type: "system", text: "Stream ended", ts: new Date() }]);
          setExecuting(false);
          axios.get(`${API}/harvest/executions/${execId}`).then(r => setSummary(r.data)).catch(() => {});
        }
      };
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setExecuting(false);
    }
  };

  const stopExecution = () => {
    eventSourceRef.current?.close();
    setExecuting(false);
    setEvents(prev => [...prev, { type: "system", text: "Execution stopped by user", ts: new Date() }]);
  };

  const completedAgents = events.filter(e => e.type === "agent").length;

  return (
    <div className="space-y-4" data-testid="harvest-playground-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Rocket className="w-4 h-4 text-amber-400" /> Execute HARVEST Division</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Select value={division} onValueChange={setDivision} disabled={executing}>
              <SelectTrigger className="w-64 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="harvest-playground-division-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIVISIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <PromptInput
                value={prompt}
                onChange={setPrompt}
                onSubmit={startExecution}
                placeholder="Ask anything — describe the supply chain optimization task"
                disabled={executing}
                loading={executing}
                maxLength={800}
                submitLabel="Execute Division"
              />
            </div>
          </div>

          {!executing && !events.length && (
            <div className="flex flex-wrap gap-2">
              {SAMPLE_PROMPTS.map((sp, i) => (
                <button key={i} onClick={() => setPrompt(sp)}
                  className="px-3 py-1.5 rounded-full text-[11px] bg-[var(--qn-surface)]/50 border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:border-amber-500/40 hover:text-[var(--qn-foreground)] transition-all"
                  data-testid={`harvest-playground-sample-${i}`}>
                  {sp.slice(0, 60)}...
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            {executing && (
              <Button onClick={stopExecution} className="bg-red-600 hover:bg-red-700" data-testid="harvest-playground-stop-btn">
                <StopCircle className="w-4 h-4 mr-2" />Stop
              </Button>
            )}
            {events.length > 0 && !executing && (
              <Button variant="outline" onClick={() => { setEvents([]); setSummary(null); setError(null); setExecutionId(null); }} data-testid="harvest-playground-clear-btn">Clear</Button>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-sm text-red-400 flex items-start gap-2" data-testid="harvest-playground-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}
            </div>
          )}
        </CardContent>
      </Card>

      {events.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              {executing ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> : <CheckCircle className="w-4 h-4 text-amber-500" />}
              Live Execution Feed
              {executing && <span className="text-xs font-mono text-amber-400 ml-2">{completedAgents} agents complete</span>}
            </CardTitle>
            {executionId && <code className="text-[10px] font-mono text-[var(--qn-foreground-muted)]">{executionId}</code>}
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[350px] overflow-y-auto">
              {events.map((evt, i) => {
                const dotColor = evt.type === "agent" ? "bg-amber-500" : evt.type === "complete" ? "bg-blue-500" : evt.type === "error" ? "bg-red-500" : evt.type === "system" ? "bg-amber-500" : "bg-[var(--qn-foreground-muted)]";
                return (
                  <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.15 }}
                    className="flex gap-3 px-4 py-2.5 border-b border-[var(--qn-border)]/20 hover:bg-[var(--qn-surface)]/20 transition-colors"
                    data-testid={`harvest-playground-event-${i}`}>
                    <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${dotColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-[var(--qn-foreground)]">{evt.text}</span>
                        {evt.model && <Badge variant="outline" className="text-[9px]">{evt.model}</Badge>}
                        {evt.tokens && <span className="text-[10px] text-[var(--qn-foreground-subtle)]">{evt.tokens} tok</span>}
                      </div>
                      {evt.detail && <p className="text-[11px] text-[var(--qn-foreground-muted)] line-clamp-2">{evt.detail}</p>}
                      {evt.mediaUrl && (
                        <a href={evt.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-amber-400 hover:underline flex items-center gap-1 mt-1">
                          <ExternalLink className="w-3 h-3" />Media
                        </a>
                      )}
                    </div>
                    <span className="text-[9px] text-[var(--qn-foreground-subtle)] shrink-0 mt-0.5">{evt.ts.toLocaleTimeString()}</span>
                  </motion.div>
                );
              })}
              <div ref={eventsEndRef} />
            </div>
          </CardContent>
        </Card>
      )}

      {summary && !executing && (
        <Card className="bg-[var(--qn-card)] border-amber-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-500" /> Execution Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Status", value: summary.status || "completed", icon: Activity },
                { label: "Agents", value: summary.agent_count || completedAgents, icon: Users },
                { label: "Tokens", value: (summary.total_tokens || 0).toLocaleString(), icon: Hash },
                { label: "Cost", value: `$${Number(summary.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: Zap },
              ].map((s, i) => (
                <div key={i} className="p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]">
                  <div className="flex items-center gap-1.5 mb-1"><s.icon className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] text-[var(--qn-foreground-muted)]">{s.label}</span></div>
                  <span className="text-sm font-mono font-bold text-[var(--qn-foreground)]">{s.value}</span>
                </div>
              ))}
            </div>
            {summary.division_summary && Object.keys(summary.division_summary).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[var(--qn-foreground-muted)] mb-2">Division Breakdown</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(summary.division_summary).map(([key, val]) => (
                    <div key={key} className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs">
                      <span className="font-medium text-amber-400 capitalize">{key}</span>
                      <span className="text-[var(--qn-foreground-muted)] ml-2">{val.agents || "?"} agents</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4">
              <p className="text-[10px] text-[var(--qn-foreground-muted)] mb-1">Equivalent API call:</p>
              <pre className="p-2 rounded bg-[#0a0e1a] text-[10px] font-mono text-amber-400 overflow-x-auto">
{`curl -X POST -H "Authorization: Bearer qng_sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({ division, prompt: prompt.slice(0, 60) + "..." })}' \\
  ${process.env.REACT_APP_BACKEND_URL}/api/harvest/execute`}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== Main HARVEST Developer Portal ====================
export default function HarvestDeveloperPortal() {
  const [activeTab, setActiveTab] = useState("playground");

  return (
    <div className="space-y-6" data-testid="harvest-developer-portal">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">HARVEST Developer Portal</h1>
          <Badge className="bg-amber-600/20 text-amber-400 border-amber-500/30 text-xs">API v1.0</Badge>
        </div>
        <p className="text-sm text-[var(--qn-foreground-muted)]">
          Manage API keys, explore endpoints, monitor usage, and integrate HARVEST food supply chain optimization into your platform.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          <TabsTrigger value="playground" data-testid="harvest-portal-tab-playground"><Rocket className="w-4 h-4 mr-2" />Playground</TabsTrigger>
          <TabsTrigger value="keys" data-testid="harvest-portal-tab-keys"><Key className="w-4 h-4 mr-2" />API Keys</TabsTrigger>
          <TabsTrigger value="docs" data-testid="harvest-portal-tab-docs"><BookOpen className="w-4 h-4 mr-2" />Docs</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="harvest-portal-tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
          <TabsTrigger value="embed" data-testid="harvest-portal-tab-embed"><Code2 className="w-4 h-4 mr-2" />Embed</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="harvest-portal-tab-webhooks"><Webhook className="w-4 h-4 mr-2" />Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="playground"><ExecutionPlayground /></TabsContent>
        <TabsContent value="keys"><APIKeysSection /></TabsContent>
        <TabsContent value="docs"><InteractiveDocsSection /></TabsContent>
        <TabsContent value="analytics"><AnalyticsSection /></TabsContent>
        <TabsContent value="embed"><EmbedWidgetSection /></TabsContent>
        <TabsContent value="webhooks"><WebhooksSection /></TabsContent>
      </Tabs>
    </div>
  );
}
